/**
 * Flat pictures for the built-in alarm guide (#436). Colours come from the
 * Mantine theme (CSS variables and `currentColor`), so they follow light and
 * dark mode; nothing is a fixed hex. SVG `fill`/`stroke` have no Mantine prop,
 * hence the `style` objects.
 */
import type { CSSProperties } from "react";

const ink: CSSProperties = { stroke: "currentColor", fill: "none" };
const surface: CSSProperties = { fill: "var(--mantine-color-body)", stroke: "currentColor" };
const accent: CSSProperties = { fill: "var(--mantine-primary-color-filled)" };
const accentLight: CSSProperties = { fill: "var(--mantine-primary-color-light)" };
const accentStroke: CSSProperties = { stroke: "var(--mantine-primary-color-filled)", fill: "none" };
const warn: CSSProperties = { fill: "var(--mantine-color-orange-filled)" };
const warnLight: CSSProperties = { fill: "var(--mantine-color-orange-light)" };
const warnStroke: CSSProperties = { stroke: "var(--mantine-color-orange-filled)", fill: "none" };

interface SvgProps {
  /** The picture's alt text (the SVG's accessible name). */
  title: string;
}

/** A tag held up to a door reader with a keypad. */
export function AlarmTagReader({ title }: SvgProps) {
  const keys = [0, 1, 2].flatMap((col) => [0, 1, 2, 3].map((row) => [146 + col * 13, 52 + row * 12] as const));
  return (
    <svg viewBox="0 0 240 120" width="100%" height="100%" role="img" aria-label={title}>
      <title>{title}</title>
      {/* The door, its frame and handle */}
      <rect x="16" y="8" width="84" height="106" rx="3" style={accentLight} />
      <rect x="16" y="8" width="84" height="106" rx="3" strokeWidth="2.5" style={ink} />
      <line x1="86" y1="62" x2="86" y2="74" strokeWidth="5" strokeLinecap="round" style={{ stroke: "currentColor" }} />
      {/* The reader on the wall */}
      <rect x="132" y="14" width="54" height="100" rx="8" strokeWidth="2.5" style={surface} />
      <circle cx="159" cy="30" r="6" style={accent} />
      <path d="M152 30a7 7 0 0 1 14 0M149 30a10 10 0 0 1 20 0" strokeWidth="1.8" strokeLinecap="round" style={accentStroke} />
      {keys.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x - 4} y={y - 3.5} width="8" height="7" rx="1.5" style={{ fill: "currentColor", opacity: 0.55 }} />
      ))}
      {/* The tag, with waves to the reader */}
      <g transform="rotate(-18 214 38)">
        <rect x="200" y="22" width="28" height="36" rx="10" style={accent} />
        <circle cx="214" cy="31" r="4" style={{ fill: "var(--mantine-color-body)" }} />
      </g>
      <path d="M194 26q-5 6 0 12M188 22q-8 10 0 20" strokeWidth="2.2" strokeLinecap="round" style={accentStroke} />
    </svg>
  );
}

/** A siren with sound waves and "15 min". */
export function AlarmSiren({ title, minutes = 15 }: SvgProps & { minutes?: number }) {
  return (
    <svg viewBox="0 0 240 120" width="100%" height="100%" role="img" aria-label={title}>
      <title>{title}</title>
      {/* Sound waves */}
      <path d="M78 46q-10 18 0 36M64 38q-17 26 0 52" strokeWidth="4" strokeLinecap="round" style={warnStroke} />
      <path d="M162 46q10 18 0 36M176 38q17 26 0 52" strokeWidth="4" strokeLinecap="round" style={warnStroke} />
      {/* The siren */}
      <path d="M92 92V62a28 28 0 0 1 56 0v30z" style={warn} />
      <path d="M104 64a16 16 0 0 1 16-16" strokeWidth="5" strokeLinecap="round" style={{ stroke: "var(--mantine-color-white)", fill: "none", opacity: 0.7 }} />
      <rect x="82" y="92" width="76" height="14" rx="4" strokeWidth="2.5" style={surface} />
      <line x1="120" y1="18" x2="120" y2="28" strokeWidth="4" strokeLinecap="round" style={warnStroke} />
      <line x1="90" y1="26" x2="96" y2="34" strokeWidth="4" strokeLinecap="round" style={warnStroke} />
      <line x1="150" y1="26" x2="144" y2="34" strokeWidth="4" strokeLinecap="round" style={warnStroke} />
      {/* "15 min" */}
      <rect x="174" y="6" width="60" height="26" rx="13" style={warnLight} />
      <rect x="174" y="6" width="60" height="26" rx="13" strokeWidth="2" style={warnStroke} />
      <text
        x="204"
        y="24"
        textAnchor="middle"
        fontSize="14"
        fontWeight="700"
        fontFamily="inherit"
        style={{ fill: "var(--mantine-color-orange-light-color)" }}
      >
        {minutes} min
      </text>
    </svg>
  );
}

/** A clock with a moon and a "turns on again" arrow: on by itself, every 2 hours. */
export function AlarmScheduleClock({ title }: SvgProps) {
  return (
    <svg viewBox="0 0 240 120" width="100%" height="100%" role="img" aria-label={title}>
      <title>{title}</title>
      {/* Clock */}
      <circle cx="110" cy="62" r="46" style={accentLight} />
      <circle cx="110" cy="62" r="46" strokeWidth="3" style={ink} />
      {[0, 90, 180, 270].map((deg) => (
        <line
          key={deg}
          x1="110"
          y1="22"
          x2="110"
          y2="29"
          strokeWidth="3"
          strokeLinecap="round"
          transform={`rotate(${deg} 110 62)`}
          style={{ stroke: "currentColor" }}
        />
      ))}
      <line x1="110" y1="62" x2="88" y2="48" strokeWidth="5" strokeLinecap="round" style={{ stroke: "currentColor" }} />
      <line x1="110" y1="62" x2="110" y2="32" strokeWidth="3.5" strokeLinecap="round" style={{ stroke: "currentColor" }} />
      <circle cx="110" cy="62" r="5" style={accent} />
      {/* Moon: outside office hours */}
      <path d="M30 14a14 14 0 1 0 16 18a11 11 0 0 1-16-18z" style={accent} />
      {/* Round arrow: it turns on again */}
      <path d="M182 40a30 30 0 1 1-6 40" strokeWidth="4" strokeLinecap="round" style={accentStroke} />
      <path d="M170 72l6 10l10-6" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" style={accentStroke} />
      <text
        x="204"
        y="67"
        textAnchor="middle"
        fontSize="15"
        fontWeight="700"
        fontFamily="inherit"
        style={{ fill: "var(--mantine-primary-color-filled)" }}
      >
        2 h
      </text>
    </svg>
  );
}
